import type { CSSProperties } from "react";
import type { Event } from "../types";
import { StatusBadge } from "../components/StatusBadge";
import { AdminActionButton, DeleteIcon, EditIcon, IconActionButton } from "../components/AdminActions";

type Props = {
  events: Event[];
  activeEventId: number;
  onSelect: (event: Event) => void;
  onNew?: () => void;
  onEdit: (event: Event) => void;
  onDelete: (event: Event) => void;
};

const dateFormat = new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });

export function EventsPage({ events, activeEventId, onSelect, onNew, onEdit, onDelete }: Props) {
  return (
    <div className="page-stack">
      <header className="page-header">
        <div><span className="eyebrow">Administración general</span><h1>Eventos</h1><p>Creá congresos y elegí cuál querés gestionar en el panel.</p></div>
        {onNew && <IconActionButton label="Nuevo evento" icon="add" tone="primary" onClick={onNew} />}
      </header>

      <section className="event-admin-summary">
        <div><span>Eventos registrados</span><strong>{events.length}</strong></div>
        <div><span>En organización</span><strong>{events.filter((event) => Boolean(event.organizeEnabled)).length}</strong></div>
        <div><span>Próximos</span><strong>{events.filter((event) => event.endDate >= new Date().toISOString().slice(0, 10)).length}</strong></div>
      </section>

      <div className="event-card-grid">
        {events.map((event) => {
          const selected = event.id === activeEventId;
          return (
            <article className={`event-admin-card ${selected ? "selected" : ""}`} key={event.id} style={{ "--event-color": event.primaryColor } as CSSProperties}>
              <div className="event-card-accent" />
              <div className="event-card-top">
                <span className={`event-monogram ${event.whatsappIconData ? "with-image" : ""}`}>{event.whatsappIconData ? <img src={event.whatsappIconData} alt={`Icono de ${event.name}`} /> : event.shortName.slice(0, 3).toUpperCase()}</span>
                <div><StatusBadge status={event.status} /><small>{event.organizeEnabled ? "Organize" : "Fuera de Organize"}{event.mobileEnabled ? " · Mobile" : ""}</small></div>
              </div>
              <h2>{event.name}</h2>
              <small className="event-admin-entity">Administrado por {event.adminEntityName || "Sin entidad asignada"}</small>
              <p>{event.venue || "Sede a confirmar"} · {event.city || "Ciudad a confirmar"}</p>
              <div className="event-date-range"><span>{dateFormat.format(new Date(event.startDate))}</span><i>→</i><span>{dateFormat.format(new Date(event.endDate))}</span></div>
              <div className="event-card-metrics">
                <div><strong>{event.sessionCount || 0}</strong><span>sesiones</span></div>
                <div><strong>{event.speakerCount || 0}</strong><span>faculty</span></div>
                <div><strong>{event.abstractCount || 0}</strong><span>trabajos</span></div>
              </div>
              <div className="event-card-actions">
                <IconActionButton label={selected ? "Evento seleccionado" : `Gestionar ${event.name}`} icon={selected ? "check" : "settings"} tone={selected ? "success" : "secondary"} onClick={() => onSelect(event)} disabled={selected} />
                <div className="admin-icon-actions"><AdminActionButton tone="edit" label={`Editar ${event.name}`} onClick={() => onEdit(event)}><EditIcon /></AdminActionButton><AdminActionButton tone="delete" label={`Eliminar ${event.name}`} onClick={() => onDelete(event)}><DeleteIcon /></AdminActionButton></div>
              </div>
            </article>
          );
        })}
      </div>
      <section className="panel event-admin-note"><span>i</span><div><strong>Los datos están separados por evento</strong><p>Las jornadas, salas, agenda, faculty y trabajos pertenecen únicamente al evento seleccionado. Al eliminar un evento también se elimina toda su información asociada.</p></div></section>
    </div>
  );
}
